import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { posApiFetch, POS_SESSION_EXPIRED_EVENT } from "@/utils/pos-api";

// POS role types mirroring the POS admin system
export const PosUserRole = {
  ADMIN: "admin" as const,
  MANAGER: "manager" as const,
  STAFF: "staff" as const,
} as const;

export type PosUserRoleType = (typeof PosUserRole)[keyof typeof PosUserRole];

// Minimal POS user shape used by admin components
export interface PosUser {
  id: number;
  email: string;
  displayName?: string | null;
  role: PosUserRoleType;
  status: string;
  imageUrl?: string | null;
  originalImageUrl?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PosAuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: PosUser | null;
  hasRole: (role: PosUserRoleType) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const PosAuthContext = createContext<PosAuthContextType | null>(null);

function normalizePosUser(data: any): PosUser | null {
  if (!data?.id || !data?.email || !data?.role) return null;
  return {
    id: data.id,
    email: data.email,
    displayName: data.displayName || data.name || null,
    role: data.role,
    status: data.status,
    imageUrl: data.imageUrl || null,
    originalImageUrl: data.originalImageUrl || null,
    lastLoginAt: data.lastLoginAt || null,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

/**
 * POS admin auth provider.
 *
 * Authentication is gated entirely by the server-issued session cookie.
 * On mount and on refresh we call /v1/pos/auth/me; if the server does not
 * confirm an active session the user is treated as logged out (fail closed).
 * No auth state is persisted in localStorage — that was the previous
 * vulnerability path (CertiK SEP-01).
 */
export function PosAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PosUser | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  // Validate session against the server on mount. The cookie sent via
  // `credentials: "include"` is the only thing that proves identity.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await posApiFetch("/v1/pos/auth/me");
        if (cancelled) return;
        if (res.ok) {
          setUser(normalizePosUser(await res.json()));
        } else {
          setUser(null);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 from posApiFetch (e.g. cookie expired mid-session) drops local
  // auth state. AdminLayoutInner then redirects to /admin/login.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(POS_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(POS_SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await posApiFetch("/v1/pos/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Login failed" }));
        return { success: false, message: err.message || "Invalid credentials" };
      }

      // Backend returns user object flat (no { user: ... } wrapper)
      const posUser = normalizePosUser(await res.json());
      if (!posUser) {
        return { success: false, message: "Invalid login response" };
      }

      setUser(posUser);
      return { success: true };
    } catch (err) {
      console.error("POS login error:", err);
      return { success: false, message: "Login failed. Please try again." };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await posApiFetch("/v1/pos/auth/logout", { method: "POST" });
    } catch {
      // ignore — clear local state regardless
    }
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (role: PosUserRoleType): boolean => {
      if (!user) return false;
      if (user.role === PosUserRole.ADMIN) return true;
      return user.role === role;
    },
    [user]
  );

  const refreshUser = useCallback(async () => {
    try {
      const res = await posApiFetch("/v1/pos/auth/me");
      if (!res.ok) {
        setUser(null);
        return;
      }
      setUser(normalizePosUser(await res.json()));
    } catch {
      // ignore — leave existing user in place; next mount will revalidate
    }
  }, []);

  return (
    <PosAuthContext.Provider
      value={{ isAuthenticated, loading, user, hasRole, login, logout, refreshUser }}
    >
      {children}
    </PosAuthContext.Provider>
  );
}

export function usePosAuth() {
  const ctx = useContext(PosAuthContext);
  if (!ctx) {
    throw new Error("usePosAuth must be used inside PosAuthProvider");
  }
  return ctx;
}
